import { AccredibleChipComponentModule } from '@accredible-frontend-v2/components/chip';
import { AccredibleSocialMedia } from '@accredible-frontend-v2/models';
import { AccredibleIconComponentModule } from '@accredible-frontend-v2/new-components/icon';
import { AccredibleLanguageService } from '@accredible-frontend-v2/services/language';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoDirective } from '@jsverse/transloco';

export interface AdditionalSocialMedia {
  provider: string;
  displayName: string;
  url: string;
}

@Component({
  standalone: true,
  selector: 'accredible-social-links',
  imports: [
    CommonModule,
    MatIconModule,
    TranslocoDirective,
    AccredibleIconComponentModule,
    AccredibleChipComponentModule,
  ],
  templateUrl: 'social-links.component.html',
  styleUrls: ['social-links.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SocialLinksComponent {
  private readonly _languageService = inject(AccredibleLanguageService);

  name = input<string>();
  socialLinks = input<AccredibleSocialMedia>();
  ariaLabel = input<string>();
  showAdditionalLinks = input<boolean>(false);
  alignment = input<'start' | 'center'>('center');
  socialNetworkClicked = output<string>();

  twitterLink = computed(() => {
    if (!this.socialLinks()?.twitter) return '';

    const twitterUsername = <string>this.socialLinks()?.twitter;
    return (
      'https://twitter.com/' +
      twitterUsername
        // Removing the @ symbol if exists
        .replace(/^@/, '')
        .replace(/^(https?:\/\/)?(www\.)?twitter\.com\//, '')
    );
  });

  additionalSocialMedia = computed(() => {
    if (!this.showAdditionalLinks() || !this.socialLinks()) {
      return [];
    }

    const excludedSocialTypes = ['linkedin', 'twitter'];
    const socialMediaList: AdditionalSocialMedia[] = [];

    for (const [provider, value] of Object.entries(this.socialLinks())) {
      if (excludedSocialTypes.includes(provider)) {
        continue;
      }

      socialMediaList.push({
        provider,
        displayName: this._languageService.translate(`social-links.${provider}`),
        url: this._ensureProtocol(provider === 'other' ? value.url : value),
      });
    }

    return socialMediaList;
  });

  private _ensureProtocol(url: string): string {
    if (!url) return url;
    return url.match(/^https?:\/\//) ? url : `https://${url}`;
  }
}
